import Message from "@/components/Message/Message";
import Table from "@/components/Table/Table";
import { CheckCircleFilled, CloseCircleFilled, InfoCircleFilled } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Progress, Spin, message } from "antd";
import axios from "axios";
import { useState } from "react";
import { StatusUpload, StatusUploadStatus } from "../../useProducts";
import styles from "./UploadProductAction.module.scss";

export default function ValidationStatus({
  fileName,
  uuid,
  onSuccesfulValidation,
}: {
  fileName: string;
  uuid: string;
  onSuccesfulValidation: () => void;
}) {
  const [status, setStatus] = useState<StatusUploadStatus>("pending");

  const getUploadStatus = useMutation({
    mutationFn: () =>
      axios
        .get<StatusUpload>("/products/upload_status?request_uuid=" + uuid)
        .then((result) => result.data),
    onSuccess: (result) => {
      setStatus(result.upload_status);
      if (result.upload_status === "success") {
        onSuccesfulValidation();
      }
      if (result.upload_status === "failed") {
        message.error("Validation failed. Try again or contact support.");
      }
    },
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["file_status", fileName],
    queryFn: () => getUploadStatus.mutateAsync(),
    refetchInterval: status === "pending" || status === "processing" ? 250 : false,
  });

  const loading = isLoading || isFetching || status === "processing" || status === "pending";

  const errors =
    data?.summary &&
    Object.entries(data.summary)
      .map(([column, errors]) =>
        Object.entries(errors).map(([error, errorStatus]) => {
          return {
            id: column + error,
            column,
            error,
            count: errorStatus.error_count,
            rows: errorStatus.rows,
          };
        })
      )
      .flat();

  const percentageCompleted =
    data?.remaining_rows !== undefined && data?.total_rows !== undefined
      ? Math.round(((data.total_rows - data.remaining_rows) / data.total_rows) * 100)
      : 0;

  return (
    <div className={styles.validate}>
      <div className={styles.report}>
        <span className={styles.title}>
          Validating {fileName} {loading && <Spin size="small" />}
        </span>
        <Progress
          status={status === "failed" ? "exception" : undefined}
          percent={percentageCompleted}
        />
        <div className={styles.metrics}>
          {data?.complete_rows ? (
            <span className={styles.metric}>
              <CheckCircleFilled style={{ color: "#52C41A" }} /> {data?.complete_rows}{" "}
              <span className={styles.entity}>rows</span>
            </span>
          ) : (
            ""
          )}
          {data?.incomplete_rows ? (
            <span className={styles.metric}>
              <CloseCircleFilled style={{ color: "#FF4D4F" }} /> {data?.incomplete_rows}{" "}
              <span className={styles.entity}>issues</span>
            </span>
          ) : (
            ""
          )}
          {data?.remaining_rows ? (
            <span className={styles.metric}>
              <InfoCircleFilled style={{ color: "#434343" }} /> {data?.remaining_rows}{" "}
              <span className={styles.entity}>remaining</span>
            </span>
          ) : (
            ""
          )}
        </div>
      </div>
      {status === "failed" && data?.summary && (
        <div className={styles.table}>
          <Message type="error">
            Validation failed. Fix the errors below and try uploading again:
          </Message>
          <Table
            columns={[
              {
                key: "column",
                label: "Column",
              },
              {
                key: "error",
                label: "Error",
              },
              {
                key: "count",
                label: "Issues",
              },
              {
                key: "rows",
                label: "Row",
                renderer: (record) => record.rows.join(", "),
              },
            ]}
            rowKey={"id"}
            data={errors}
          />
        </div>
      )}
    </div>
  );
}
